/**
 * Cart Recovery Script
 *
 * Runs on the /pages/recover Shopify page. Reads a single base64-encoded
 * blob from the `data` query parameter containing cart items, checkout
 * pre-fill fields, and an optional discount code. Clears the current cart,
 * re-adds all items in parallel (preserving line item properties), then
 * redirects the customer to checkout with all pre-fill params applied.
 *
 * Expected URL shape:
 *   /pages/recover?data=<base64>
 *
 * The base64 blob decodes to:
 *   {
 *     items:    CartItem[]
 *     checkout: CheckoutFields
 *     discount: string
 *   }
 *
 * Encoding the entire payload as one blob avoids any URI encoding issues
 * with bracket keys or special characters in query parameters entirely.
 */

import { fetchCartVersionData, findStaleLines, hasVersionsToEnforce } from '../base/cart-version';

(async (): Promise<void> => {
  'use strict';

  interface CartItem {
    variantId: number;
    quantity: number;
    properties: Record<string, string>;
  }

  interface CheckoutFields {
    email:      string;
    first_name: string;
    last_name:  string;
    address1:   string;
    city:       string;
    province:   string;
    country:    string;
    zip:        string;
  }

  interface RecoveryPayload {
    items:    CartItem[];
    checkout: CheckoutFields;
    discount: string;
  }

  if (!window.location.pathname.includes('/pages/recover')) return;

  // Set the window title
  document.title = 'Please Wait... | Abbott Lyon';

  // Overlay the entire page immediately so the customer sees a loading state
  // rather than the raw page content while the cart is being restored
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: var(--color-white);
    z-index: 99999;
    gap: var(--spacing-base);
    padding: 0 32px;
    text-align: center;
  `;

  const markersMarkUrl = (window as unknown as { markersMarkUrl?: string }).markersMarkUrl;
  overlay.innerHTML = `
    <style>
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    </style>
    ${markersMarkUrl ? `<img src="${markersMarkUrl}" alt="Abbott Lyon" style="max-width: 32px; height: auto; animation: spin 0.8s linear infinite;" />` : ''}
    <h2>Loading...</h2>
  `;
  document.body.appendChild(overlay);

  // Show an error message in the overlay and stop — does not redirect
    const showError = (message: string): void => {
      console.error('[cart-recovery]', message);
      document.title = `${message} | Abbott Lyon`

      let seconds = 5;

      const render = () => {
        overlay.innerHTML = `
          <h2>${message}</h2>
          <p>Redirecting to homepage in ${seconds} second${seconds !== 1 ? 's' : ''}...</p>
          <a class="button button--secondary" href="/">Go home &rsaquo;</a>
        `;
      };

      render();

      const interval = setInterval(() => {
        seconds--;
        if (seconds <= 0) {
          clearInterval(interval);
          window.location.href = '/';
        } else {
          render();
        }
      }, 1000);
    };

  const encoded = new URL(window.location.href).searchParams.get('data');

  if (!encoded) {
    showError('No cart data found. Please return to your email and try the link again.');
    return;
  }

  // Decode the URL-safe base64 blob back into the full payload object.
  // Inverts btoa(unescape(encodeURIComponent())) used in the Klaviyo script:
  //   atob() → reverse btoa()
  //   escape() → reverse unescape()
  //   decodeURIComponent() → reverse encodeURIComponent()
  let payload: RecoveryPayload;
  try {
    const padded = encoded.replace(/-/g, '+').replace(/_/g, '/')
      + '=='.slice(0, (4 - (encoded.length % 4)) % 4);
    payload = JSON.parse(decodeURIComponent(escape(atob(padded)))) as RecoveryPayload;
    if (!Array.isArray(payload.items) || payload.items.length === 0) throw new Error('Empty payload');
  } catch (err) {
    showError('We couldn\'t restore your cart. Please try the link again or contact support.');
    return;
  }

  const { items, checkout, discount } = payload;

  // Clear the existing cart before restoring so we don't merge with stale items
  await fetch('/cart/clear', { method: 'GET' });

  // Add a single item to the Shopify cart, retrying up to 3 times on 429s
  const addItem = async (item: CartItem, attempt = 1): Promise<unknown> => {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id:         item.variantId,
        quantity:   item.quantity   ?? 1,
        properties: item.properties ?? {},
      }),
    });
    if (res.status === 429 && attempt <= 3) {
      await new Promise<void>(r => setTimeout(r, 600 * attempt));
      return addItem(item, attempt + 1);
    }
    if (!res.ok) {
      const json = await res.json().catch(() => ({})) as { description?: string };
      throw new Error(json.description ?? `HTTP ${res.status} for variant ${item.variantId}`);
    }
    return res.json();
  };

  // Add items sequentially — Shopify rejects concurrent cart requests.
  const results: (unknown | Error)[] = [];
  for (let i = 0; i < items.length; i++) {
    const result = await addItem(items[i]).catch((err: Error) => {
      console.warn(`[cart-recovery] Item ${i + 1} failed:`, err.message);
      return err;
    });
    results.push(result);
  }

  // If any items failed, clear everything out to avoid
  // orphaned child lines.
  const anyFailed = results.some(r => r instanceof Error);
  if (anyFailed) {
    showError('Sorry, we couldn\'t automatically re-add the items in your email to the cart.');
    await fetch('/cart/clear', { method: 'GET' }).catch((err: Error) => {
      console.warn(`[cart-recovery] Failed to clear cart after partial recovery`)
    });    
    return;
  }

  // ALBAU-855: personalised items saved under an old cart version must not go straight to
  // checkout. Send the customer to the cart instead, where they are flagged and checkout is blocked
  // until they are removed. Checkout pre-fill is lost; the discount is kept.
  try {
    const versionData = await fetchCartVersionData();
    if (hasVersionsToEnforce(versionData)) {
      const cart = (await (await fetch('/cart.js')).json()) as { items?: [] };
      if (findStaleLines(cart.items ?? [], versionData.versions).length > 0) {
        window.location.href = discount ? `/discount/${encodeURIComponent(discount)}?redirect=/cart` : '/cart';
        return;
      }
    }
  } catch (err) {
    // Don't block recovery if the check fails; carry on to checkout as before.
    console.warn('[cart-recovery] Cart version check failed:', (err as Error).message);
  }

  // Build the /checkout URL with pre-fill params from the decoded payload.
  // All values came from the base64 blob so no URI encoding issues here —
  // we just encodeURIComponent the values as normal.
  const enc  = encodeURIComponent;
  const parts: string[] = [];

  if (checkout?.email)      parts.push(`checkout[email]=${enc(checkout.email)}`);
  if (checkout?.first_name) parts.push(`checkout[shipping_address][first_name]=${enc(checkout.first_name)}`);
  if (checkout?.last_name)  parts.push(`checkout[shipping_address][last_name]=${enc(checkout.last_name)}`);
  if (checkout?.address1)   parts.push(`checkout[shipping_address][address1]=${enc(checkout.address1)}`);
  if (checkout?.city)       parts.push(`checkout[shipping_address][city]=${enc(checkout.city)}`);
  if (checkout?.province)   parts.push(`checkout[shipping_address][province]=${enc(checkout.province)}`);
  if (checkout?.country)    parts.push(`checkout[shipping_address][country]=${enc(checkout.country)}`);
  if (checkout?.zip)        parts.push(`checkout[shipping_address][zip]=${enc(checkout.zip)}`);
  if (discount)             parts.push(`discount=${enc(discount)}`);

  const qs = parts.join('&');

  window.location.href = `/checkout${qs ? '?' + qs : ''}`;
})();